import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    CircularProgress,
    Stack,
    Tooltip,
    Divider,
} from '@mui/material';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BlockIcon from '@mui/icons-material/Block';
import RefreshIcon from '@mui/icons-material/Refresh';
import { TokenContext } from '../Context/TokenContext';
import { UserContext } from '../Context/UserContext';
import { listQuotes, revokeQuote, regenerateQuoteLink } from '../Utils/quotesApi';
import { formatEUR } from '../Utils/pdfExport';

const API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8080';

const STATUS_LABELS = {
    pending: { label: 'En attente', color: 'warning' },
    paid: { label: 'Payé', color: 'success' },
    active: { label: 'Abonnement actif', color: 'success' },
    declined: { label: 'Décliné', color: 'error' },
    expired: { label: 'Expiré', color: 'default' },
    revoked: { label: 'Révoqué', color: 'default' },
    canceled: { label: 'Résilié', color: 'default' },
    failed: { label: 'Paiement échoué', color: 'error' },
};

const TYPE_MAP = { individual: 'Particulier', company: 'Entreprise' };

// client_management stocke clients ET prospects dans la même table `client`
// (type_client: 'individual' | 'company' | 'prospect', voir model/Client.js)
// — les deux endpoints /api/clients et /api/prospects renvoient donc la même
// forme brute (id_client, type_client, raison_sociale, nom,
// Utilisateurs[].{prenom,nom,email,phone}), même transformation que
// ClientsPage.js/ProspectsPage.js, nécessaire pour afficher un nom
// exploitable dans la colonne "Destinataire".
function mapPerson(c) {
    const primaryUser = c.Utilisateurs?.[0];
    return {
        id: c.id_client,
        name: c.type_client === 'company' ? (c.raison_sociale || c.nom || '—') : (c.nom || c.raison_sociale || '—'),
        contact: primaryUser ? `${primaryUser.prenom || ''} ${primaryUser.nom || ''}`.trim() || '—' : '—',
        email: primaryUser?.email || '—',
        phone: primaryUser?.phone || '—',
        type: TYPE_MAP[c.type_client] || c.type_client || '—',
    };
}

function quoteTotal(quote) {
    return (quote.items || []).reduce((sum, item) => sum + item.amount, 0);
}

export default function QuotesPage() {
    const { accessToken } = useContext(TokenContext);
    const { userInfo } = useContext(UserContext);
    const canManageQuotes = userInfo?.is_super_admin === true || (userInfo?.permissions || []).includes('quotes.manage');
    const navigate = useNavigate();

    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [clients, setClients] = useState([]);
    const [prospects, setProspects] = useState([]);

    const [selectedQuote, setSelectedQuote] = useState(null);
    const [regeneratedLink, setRegeneratedLink] = useState(null); // { ephemeral_url }
    const [copied, setCopied] = useState(false);

    // Un même id_client peut être un client ou un prospect selon le devis —
    // une seule table de correspondance pour la colonne "Destinataire".
    const personById = useMemo(() => {
        const map = new Map();
        [...clients, ...prospects].forEach((p) => map.set(p.id, p));
        return map;
    }, [clients, prospects]);

    const fetchQuotes = useCallback(() => {
        if (!accessToken) return;
        setLoading(true);
        listQuotes(accessToken)
            .then(setQuotes)
            .catch(() => setError('Impossible de charger les devis'))
            .finally(() => setLoading(false));
    }, [accessToken]);

    useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

    useEffect(() => {
        if (!accessToken) return;
        const headers = { Authorization: `Bearer ${accessToken}` };
        axios.get(`${API_URL}/api/clients`, { headers })
            .then((res) => setClients((res.data || []).map(mapPerson)))
            .catch(() => {});
        axios.get(`${API_URL}/api/prospects`, { headers })
            .then((res) => setProspects(
                // "mort" (lead abandonné) et "converti" (déjà un client, à
                // choisir via "Client existant") n'ont pas leur place ici.
                (res.data || [])
                    .filter((p) => !['mort', 'converti'].includes(p.statut_prospect))
                    .map(mapPerson)
            ))
            .catch(() => {});
    }, [accessToken]);

    const handleRevoke = (quote) => {
        if (!window.confirm(`Révoquer le devis "${quote.title}" ? Le lien deviendra inutilisable.`)) return;
        revokeQuote(accessToken, quote.id).then(fetchQuotes).catch(() => setError('Révocation impossible'));
    };

    const handleRegenerate = (quote) => {
        regenerateQuoteLink(accessToken, quote.id)
            .then((data) => setRegeneratedLink({ ephemeral_url: data.ephemeral_url }))
            .catch(() => setError('Régénération du lien impossible'));
    };

    const handleCopy = () => {
        if (!regeneratedLink) return;
        navigator.clipboard.writeText(regeneratedLink.ephemeral_url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const closeRegenerateDialog = () => {
        setRegeneratedLink(null);
        setCopied(false);
        fetchQuotes();
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={2} mb={3}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <RequestQuoteOutlinedIcon color="primary" sx={{ mr: 2, fontSize: 32 }} />
                    <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                        Devis
                    </Typography>
                </Box>
                {canManageQuotes && (
                    <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/quotes/new')}>
                        Nouveau devis
                    </Button>
                )}
            </Stack>

            <Alert severity="info" sx={{ mb: 3 }}>
                Un devis génère un lien de paiement éphémère (Stripe, unique ou récurrent), pouvant
                couvrir plusieurs licences, à transmettre vous-même au client ou prospect. Le lien
                n'est affiché qu'une seule fois, à la création (ou à la régénération) — copiez-le
                tout de suite.
            </Alert>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Titre</TableCell>
                            <TableCell>Destinataire</TableCell>
                            <TableCell>Lignes</TableCell>
                            <TableCell>Montant</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Statut</TableCell>
                            <TableCell>Expire le</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                    <CircularProgress size={24} />
                                </TableCell>
                            </TableRow>
                        ) : quotes.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    <Typography color="text.secondary">Aucun devis pour le moment</Typography>
                                </TableCell>
                            </TableRow>
                        ) : quotes.map((quote) => {
                            const status = STATUS_LABELS[quote.status] || { label: quote.status, color: 'default' };
                            const items = quote.items || [];
                            return (
                                <TableRow key={quote.id} hover onClick={() => setSelectedQuote(quote)} sx={{ cursor: 'pointer' }}>
                                    <TableCell>{quote.title}</TableCell>
                                    <TableCell>
                                        {(() => {
                                            const person = quote.id_client ? personById.get(quote.id_client) : null;
                                            const name = person?.name || quote.recipient_name || (quote.id_client ? `Client #${quote.id_client}` : '—');
                                            const email = person?.email !== '—' ? person?.email : quote.recipient_email;
                                            return (
                                                <Box>
                                                    <Typography variant="body2">{name}</Typography>
                                                    {email && (
                                                        <Typography variant="caption" color="text.secondary">{email}</Typography>
                                                    )}
                                                </Box>
                                            );
                                        })()}
                                    </TableCell>
                                    <TableCell>{items.length}</TableCell>
                                    <TableCell>
                                        {formatEUR(quoteTotal(quote) / 100)}
                                        {quote.type === 'subscription' ? ` / ${quote.interval}` : ''}
                                    </TableCell>
                                    <TableCell>{quote.type === 'subscription' ? 'Récurrent' : 'Unique'}</TableCell>
                                    <TableCell><Chip label={status.label} color={status.color} size="small" /></TableCell>
                                    <TableCell>
                                        {quote.expires_at ? new Date(quote.expires_at).toLocaleDateString('fr-FR') : '—'}
                                    </TableCell>
                                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                        {quote.status === 'pending' && canManageQuotes && (
                                            <>
                                                <Tooltip title="Régénérer le lien">
                                                    <IconButton size="small" onClick={() => handleRegenerate(quote)}>
                                                        <RefreshIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Révoquer">
                                                    <IconButton size="small" color="error" onClick={() => handleRevoke(quote)}>
                                                        <BlockIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={!!selectedQuote} onClose={() => setSelectedQuote(null)} maxWidth="sm" fullWidth>
                {selectedQuote && (
                    <>
                        <DialogTitle>{selectedQuote.title}</DialogTitle>
                        <DialogContent>
                            <Stack spacing={1.5}>
                                {selectedQuote.description && (
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                        {selectedQuote.description}
                                    </Typography>
                                )}
                                <Divider />
                                {(selectedQuote.items || []).map((item) => (
                                    <Box key={item.id}>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="body2">{item.title}</Typography>
                                            <Typography variant="body2">{formatEUR(item.amount / 100)}</Typography>
                                        </Stack>
                                        {item.description && (
                                            <Typography variant="caption" color="text.secondary">{item.description}</Typography>
                                        )}
                                        {item.application_client_id && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                Application : {item.application_client_id}
                                                {item.application_option_ids?.length
                                                    ? ` (+ ${item.application_option_ids.length} option${item.application_option_ids.length > 1 ? 's' : ''})`
                                                    : ''}
                                            </Typography>
                                        )}
                                        {item.authorizations?.length > 0 && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                Permissions : {item.authorizations.join(', ')}
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                                <Divider />
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="subtitle2">Total</Typography>
                                    <Typography variant="subtitle2">
                                        {formatEUR(quoteTotal(selectedQuote) / 100)}
                                        {selectedQuote.type === 'subscription' ? ` / ${selectedQuote.interval}` : ''}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setSelectedQuote(null)}>Fermer</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <Dialog open={!!regeneratedLink} onClose={closeRegenerateDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Lien régénéré</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Alert severity="success">
                            Copiez ce lien maintenant — il ne sera plus jamais affiché.
                        </Alert>
                        <TextField
                            fullWidth
                            size="small"
                            value={regeneratedLink?.ephemeral_url || ''}
                            InputProps={{ readOnly: true }}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<ContentCopyIcon />}
                            onClick={handleCopy}
                        >
                            {copied ? 'Copié !' : 'Copier le lien'}
                        </Button>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeRegenerateDialog}>Fermer</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
